import { listRunEvents } from '@/generated/hooks/bishop/events/v1/api';
import type {
  Commonv1Value,
  V1FunctionCallContextInputArgs,
  V1RunEvent,
} from '@/generated/hooks/bishop/models';
import type { Commonv1Location } from '@/generated/hooks/grimoire/models/commonv1Location';
import type { V1Run, V1RunState } from '@/generated/hooks/jeeves/models';
import { isBrowserInstance, resolveCommonValue } from '@/modules/outputs/utils';
import { captureException } from '@/modules/sentry';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  ERetryStrategy,
  EStreamProtocol,
  EStreamState,
  type IStreamError,
  useStream,
} from '@/shared/streaming';
import { FilterBuilder, serializeParams } from '@/shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getListExceptionsQueryKey } from '@/generated/hooks/astral/v1/api';
import { convertKeysToSnakeCase } from '@/shared/utils/convert-keys';
import { payloadHasBrowserInstance } from '../utils/run-log-utils';
import { isRunEffectivelyFinal, isRunInFinalState } from '../utils/run-utils';

/** The external book procedure a `BookFunctionCall` node invoked. */
export interface IBookFunctionRef {
  /** Book the procedure belongs to; keys the book lookup. */
  bookName?: string;
  /** Procedure invoked. */
  functionName?: string;
  /** Connection the call ran through, when it needed one. */
  connectionSpyIdentifier?: string;
}

/**
 * A function-call step extracted from the execution-journal SSE stream.
 *
 * One per `FunctionCall` `nodeExecuted` event inside `main`. Everything comes
 * from the node's `function_call_context`: the function name, its named input
 * arguments, and its return value (the step's output).
 *
 * Also used for `BookFunctionCall` nodes, which carry an identical
 * `function_call_context` and are told apart by `bookFunction` being set. Those
 * never appear in `events`; each hangs off its parent step's `externalCalls`.
 */
export interface IFunctionCallEvent {
  /** Node id of the call site; shared by every iteration of a loop. */
  nodeId: string;
  /** Function name from `function_call_context.function_name`. */
  functionName: string;
  /** Named input arguments from `function_call_context.input_args`. */
  inputs?: V1FunctionCallContextInputArgs;
  /** The function's return value from `function_call_context.return_value`. */
  returnValue?: Commonv1Value;
  /** SPy code span of the call site in `main`; used for byte-based progress. */
  codeLocation?: Commonv1Location;
  /**
   * ISO timestamp from the `node_executed` journal `event_time` — the moment the
   * step finished. Paired with `startedTimestamp` to compute the step duration.
   */
  timestamp?: string;
  /**
   * ISO timestamp from the matching `node_started` journal `event_time` (same
   * `node_id`) — the moment the step started. Undefined until the started event
   * is seen; a step's duration is `startedTimestamp → timestamp`.
   */
  startedTimestamp?: string;
  /** Set only on a `BookFunctionCall` node; distinguishes a call from a step. */
  bookFunction?: IBookFunctionRef;
  /**
   * External book calls made while this step was executing, in execution order.
   *
   * Carries the payload the step's own return value drops.
   */
  externalCalls?: IFunctionCallEvent[];
}

/**
 * Identity of one invocation of a call site, stable across stream replays.
 *
 * @param event - A function-call or book-call event
 * @returns A key unique to this invocation
 */
export function invocationKey(event: IFunctionCallEvent): string {
  return `${event.nodeId}@${event.timestamp ?? ''}`;
}

export enum ENodeKind {
  /** A call site in `main`; one run-log step. */
  FunctionCall = 'FunctionCall',
  /** A call into an external book, from a step body or a helper it calls. */
  BookFunctionCall = 'BookFunctionCall',
}

/** True for a node kind the journal filter admits. */
function isJournalNodeKind(kind: string | undefined): kind is ENodeKind {
  return kind === ENodeKind.FunctionCall || kind === ENodeKind.BookFunctionCall;
}

/**
 * How long after a run goes terminal we keep the journal tail open with no new
 * events before closing it. The journal stream never receives its own
 * `completed` marker (the `node_kind` server filter drops it),
 * so this idle window is the close signal. Each new event resets it, so a burst
 * of final events is never cut off mid-delivery.
 */
const JOURNAL_IDLE_CLOSE_MS = 30_000;

/** Stable empty list, so a step with no pending calls keeps one reference. */
const NO_EXTERNAL_CALLS: IFunctionCallEvent[] = [];

/** Shape of one SSE data message after camelCase→snake_case conversion. */
interface IStreamMessage {
  event?: {
    execution_journal?: {
      event_time?: string;
      completed?: unknown;
      node_executed?: {
        /** An `ENodeKind` value, or any other kind the runtime may emit. */
        node_kind?: string;
        node_id?: string;
        code_location?: Commonv1Location;
        function_call_context?: {
          function_name?: string;
          input_args?: V1FunctionCallContextInputArgs;
          return_value?: Commonv1Value;
        };
        /** `output` isn't read: it duplicates the return value. */
        book_function_call?: {
          book_function?: {
            book_name?: string;
            function_name?: string;
            connection_spy_identifier?: string;
          };
          /** The call's own arguments; `input_args` is often empty for one. */
          inputs?: V1FunctionCallContextInputArgs;
        };
      };
      /**
       * Emitted when a node begins executing — same `node_id` as the eventual
       * `node_executed`. Its wrapping `event_time` is the step's start; we pair
       * it to the executed event to derive the step duration. Passes the same
       * server-side `node_kind`/line filter as `node_executed`.
       *
       * `code_location` is the step's SPy call site — the same span
       * `node_executed` carries — so a step that has started but not finished
       * can still be located in the AOP, before it has produced any output.
       */
      node_started?: {
        node_kind?: string;
        node_id?: string;
        code_location?: Commonv1Location;
      };
    };
    run_update?: {
      run?: V1Run;
    };
  };
  id?: string;
}

/** Parses an SSE `data:` payload into the snake_case shape we consume. */
export function parseMessage(raw: string): IStreamMessage {
  const parsed = JSON.parse(raw) as Record<string, unknown>;
  return convertKeysToSnakeCase(parsed) as IStreamMessage;
}

/**
 * True if a function-call event's input arguments or return value contain a
 * browser instance.
 *
 * Analogous to `sectionHasBrowserInstance` for the simplified manifest path
 * — used as the `shouldAutoExpand` predicate so a browser-bearing step's
 * player slot mounts as soon as it appears.
 */
export function functionCallHasBrowserInstance(
  event: IFunctionCallEvent,
): boolean {
  if (
    payloadHasBrowserInstance(event.inputs) ||
    (event.returnValue != null &&
      isBrowserInstance(resolveCommonValue(event.returnValue)))
  ) {
    return true;
  }
  // The handle usually comes from the book call, not the step wrapping it.
  return (event.externalCalls ?? []).some(functionCallHasBrowserInstance);
}

/** Returns the new run state embedded in a `run_update` SSE message, if any. */
export function extractRunUpdate(message: IStreamMessage): V1Run | null {
  return message.event?.run_update?.run ?? null;
}

/**
 * 0-based line range of `main` from the manifest: `startLine` inclusive,
 * `endLine` one past the last line. Drives the journal stream's server-side
 * filter.
 */
export interface IMainBounds {
  startLine: number;
  endLine: number;
}

/**
 * AIP-160 filter scoping the journal stream to the node kinds the run log
 * renders, as a disjunction: `FunctionCall` within main's line range (the steps)
 * OR `BookFunctionCall` at any line (the external calls, which execute inside
 * step bodies and helpers). Every other node kind is dropped server-side.
 *
 * The streaming endpoint scopes to journal events via the `X-Event-Type` header,
 * but the `listRunEvents` REST endpoint has no such header — it needs the event
 * type in the filter. Pass `eventType: true` for the REST path so the filter
 * also carries `event_type = "execution_journal"`.
 */
export function buildJournalScopeFilter(
  bounds: IMainBounds,
  options?: { eventType?: boolean },
): string {
  const builder = FilterBuilder.create().or(
    FilterBuilder.create()
      .greaterThanOrEqual('start_line', bounds.startLine)
      .lessThanOrEqual('end_line', bounds.endLine)
      .equals('node_kind', ENodeKind.FunctionCall),
    FilterBuilder.create().equals('node_kind', ENodeKind.BookFunctionCall),
  );
  if (options?.eventType) {
    builder.equals('event_type', 'execution_journal');
  }
  return builder.build();
}

/**
 * Returns the call for a `FunctionCall` or `BookFunctionCall` `nodeExecuted`
 * event with a named `function_call_context`, else null.
 *
 * Both node kinds carry an identical `function_call_context`, so one extractor
 * serves both; a `BookFunctionCall` additionally gets `bookFunction` set, which
 * the caller keys on to attach it to its parent step instead of appending it as a
 * step. Line scoping is enforced server-side; dedup is the caller's job.
 */
export function extractFunctionCallEvent(
  message: IStreamMessage,
): IFunctionCallEvent | null {
  const journal = message.event?.execution_journal;
  const node = journal?.node_executed;
  const fcc = node?.function_call_context;
  if (
    !node ||
    !isJournalNodeKind(node.node_kind) ||
    !node.node_id ||
    !fcc?.function_name
  ) {
    return null;
  }
  const bookFunction = node.book_function_call?.book_function;
  const bookInputs = node.book_function_call?.inputs;
  return {
    nodeId: node.node_id,
    functionName: fcc.function_name,
    inputs:
      bookInputs && Object.keys(bookInputs).length > 0
        ? bookInputs
        : fcc.input_args,
    returnValue: fcc.return_value,
    codeLocation: node.code_location,
    timestamp: journal?.event_time,
    ...(node.node_kind === ENodeKind.BookFunctionCall && {
      bookFunction: {
        bookName: bookFunction?.book_name,
        functionName: bookFunction?.function_name,
        connectionSpyIdentifier: bookFunction?.connection_spy_identifier,
      },
    }),
  };
}

/**
 * A step that has begun executing, from a `FunctionCall` `node_started` event.
 *
 * Unlike {@link IFunctionCallEvent} (emitted on `node_executed`, i.e. once the
 * step has *finished*), this is the only signal that a step is executing right
 * now. It carries no function name or output — those live on the executed event
 * — but `codeLocation` is enough to locate the step in the AOP.
 */
export interface INodeStartedEvent {
  /** Node id, shared with the eventual `node_executed` for the same step. */
  nodeId: string;
  /** ISO timestamp from the journal `event_time` — when the step started. */
  startedTimestamp: string;
  /** The step's SPy call site, used to map it back to the AOP. */
  codeLocation?: Commonv1Location;
  /** Distinguishes a started step from a started external call. */
  nodeKind: ENodeKind;
}

/**
 * Returns the started marker for a `FunctionCall` or `BookFunctionCall`
 * `node_started` journal event, else null.
 *
 * Its wrapping `event_time` is the node's start time; the caller pairs it to the
 * matching `node_executed` (same `node_id`) to derive the duration. Other node
 * kinds and non-started messages yield null.
 */
export function extractNodeStartedEvent(
  message: IStreamMessage,
): INodeStartedEvent | null {
  const journal = message.event?.execution_journal;
  const started = journal?.node_started;
  const startedTimestamp = journal?.event_time;
  if (
    !started ||
    !isJournalNodeKind(started.node_kind) ||
    !started.node_id ||
    !startedTimestamp
  ) {
    return null;
  }
  return {
    nodeId: started.node_id,
    startedTimestamp,
    codeLocation: started.code_location,
    nodeKind: started.node_kind,
  };
}

/**
 * Same extraction as {@link extractFunctionCallEvent}, but for a `V1RunEvent`
 * returned by the `listRunEvents` REST endpoint (the non-streaming twin of the
 * journal SSE). Both carry the identical snake_case `execution_journal` shape,
 * so we wrap the REST event in the stream-message envelope and delegate.
 */
export function extractFunctionCallEventFromRunEvent(
  runEvent: V1RunEvent,
): IFunctionCallEvent | null {
  if (!runEvent.execution_journal) return null;
  // The REST `execution_journal` (V1ExecutionJournal) and the SSE message's
  // inline journal type are structurally identical on the fields we read; cast
  // through the stream-message envelope so the single extractor serves both.
  return extractFunctionCallEvent({
    event: { execution_journal: runEvent.execution_journal },
  } as IStreamMessage);
}

/**
 * REST twin of {@link extractNodeStartedEvent} — reads the `node_started` marker
 * from a `V1RunEvent` (paginated `listRunEvents` history). Delegates to the SSE
 * extractor via the stream-message envelope, same as the executed path.
 */
export function extractNodeStartedEventFromRunEvent(
  runEvent: V1RunEvent,
): INodeStartedEvent | null {
  if (!runEvent.execution_journal) return null;
  return extractNodeStartedEvent({
    event: { execution_journal: runEvent.execution_journal },
  } as IStreamMessage);
}

/**
 * Parameters for the useRunJournalEvents hook.
 */
interface IUseRunJournalEventsParams {
  automationId: string | undefined;
  runId: string | undefined;
  /**
   * Called with each `run_update.run` as state transitions stream in. Lets the
   * caller refresh `runData` (e.g. for awaiting-guidance / terminal handling)
   * without polling. The stream auto-closes once the run is terminal.
   */
  onRunStateUpdate?: (run: V1Run) => void;
  /**
   * Line range of `main` from the code manifest. Scopes the step arm of the
   * journal filter so a step function's internal calls don't each surface as
   * another step; external book calls are admitted by kind regardless of line.
   * The journal stream waits to open until this resolves.
   *
   * Must be a stable reference (memoized): the snapshot effect keys on it, so a
   * fresh object per render restarts the snapshot and the journal tail never
   * settles enough to open.
   */
  mainBounds?: IMainBounds;
  /**
   * Current run state. Lets the REST-history fallback detect a terminal run
   * independently of the run_update SSE, so a completed run still backfills
   * its steps even if both streams opened too late to replay anything.
   */
  runState?: V1RunState;
  /**
   * Total executable steps (from GetRun; streamed updates omit `executable_steps`).
   * With each update's `active_step_index`, distinguishes an intermediate step
   * `completed` from the run finishing. 0 when unknown (treated as terminal).
   */
  totalSteps?: number;
}

/**
 * Return value for the useRunJournalEvents hook.
 */
interface IUseRunJournalEventsResult {
  /** Function-call step events in arrival order, deduped by `nodeId`. */
  events: IFunctionCallEvent[];
  /**
   * The step executing right now: the most recent `node_started` with no
   * matching `node_executed` yet. Null when every started step has finished.
   *
   * `events` only ever holds *finished* steps, so this is the sole handle on the
   * in-flight one. Callers must still gate on run state — a run that dies or
   * pauses mid-step leaves its last started event unmatched.
   */
  runningStep: INodeStartedEvent | null;
  /**
   * External calls made by `runningStep`, which has not completed and so is
   * absent from `events`. Empty once every started step has finished.
   */
  runningStepExternalCalls: IFunctionCallEvent[];
  /** True while the SSE connection is opening / open / reconnecting. */
  isStreaming: boolean;
  /**
   * True until the REST history snapshot settles, success or failure.
   *
   * Distinct from `isStreaming`, which is always false for a terminal run —
   * without this, a completed run looks "loaded and empty" for the whole
   * duration of its snapshot fetch.
   */
  isLoading: boolean;
  /**
   * Set when the history snapshot failed, or when the live tail gave up after
   * exhausting its retries. Non-null with a non-empty `events` list means the
   * data on screen may be incomplete rather than absent.
   */
  error: Error | null;
  /** Re-runs the history snapshot and re-opens the tail. */
  retry: () => void;
}

/**
 * Streams `execution_journal` events for a run and exposes the function-call
 * steps with their inputs/outputs.
 *
 * Snapshot-then-tail (no web worker, no IndexedDB), intended for newer
 * manifest-based automations where event volume is small:
 *   1. Always load the full filtered history over REST (`listRunEvents`) — this
 *      is the authoritative source and is paginated + deterministic, so it can
 *      never be truncated or raced like an SSE replay.
 *   2. If the run is still live, open the journal `events:stream` with
 *      `Last-Event-ID` set to the newest event_time from the snapshot, so the
 *      stream resumes with ONLY newer events (the live tail). Terminal runs skip
 *      the stream entirely — the snapshot already has everything.
 * Dedup by `nodeId` (book calls by {@link invocationKey}) makes the snapshot /
 * tail boundary (and a terminal reconcile) idempotent.
 */
export function useRunJournalEvents({
  automationId,
  runId,
  onRunStateUpdate,
  mainBounds,
  runState,
  totalSteps = 0,
}: IUseRunJournalEventsParams): IUseRunJournalEventsResult {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  // Ref keeps the token getter fresh without retriggering the stream effect.
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  // Ref so handleRunUpdate stays referentially stable while still seeing the
  // latest callback identity from the parent.
  const onRunStateUpdateRef = useRef(onRunStateUpdate);
  onRunStateUpdateRef.current = onRunStateUpdate;

  // Ref so the snapshot completion can read the current run state to decide
  // whether the run was still live (and therefore worth tailing).
  const runStateRef = useRef(runState);
  runStateRef.current = runState;

  // Kept in a ref so the stable run_update handler reads the latest count.
  const totalStepsRef = useRef(totalSteps);
  totalStepsRef.current = totalSteps;

  const [events, setEvents] = useState<IFunctionCallEvent[]>([]);
  const seenInvocationKeysRef = useRef<Set<string>>(new Set());

  // `node_started` arrives before its `node_executed` twin (same node id), so we
  // stash each step's start time here and stamp it onto the step when the
  // executed event is appended. If the started event ever lands after the step
  // is already in state (out-of-order REST replay), `recordStarted` patches
  // the existing step instead.
  const startedTimesRef = useRef<Map<string, string>>(new Map());

  // Latest `node_executed` time per step, to tell a looped start from a replay.
  const executedTimesRef = useRef<Map<string, string>>(new Map());

  // Steps that emitted `node_started` but no `node_executed` yet — i.e. the ones
  // executing right now. Normally at most one, since main's steps run
  // sequentially, but kept as a list so an unmatched entry can't mask a later
  // step's start. Held in state (not a ref) because the UI marks the running
  // step in the AOP and must re-render when it moves.
  //
  // Only ever holds steps; an external call's start is recorded for its duration.
  const [startedSteps, setStartedSteps] = useState<INodeStartedEvent[]>([]);

  // Ref mirror of `startedSteps` ids: `appendEvent` needs the enclosing step
  // synchronously, before React commits `recordStarted`'s queued update. Also
  // holds a later loop iteration of a step already in state.
  const openStepIdsRef = useRef<string[]>([]);

  // Attribution fallback for an external call arriving with no step open.
  const lastStepIdRef = useRef<string | null>(null);

  // External calls waiting for their parent step to land: the stack unwinds
  // inner-first, so a call is seen while its step is still open. Keyed by parent
  // step node id, drained by `appendEvent`.
  const pendingExternalCallsRef = useRef<Map<string, IFunctionCallEvent[]>>(
    new Map(),
  );

  // State mirror of the buffer. A step that dies mid-flight never emits
  // `node_executed`, so its buffered calls are never drained — and those are the
  // ones worth seeing. The ref stays the source of truth for synchronous reads.
  const [pendingExternalCalls, setPendingExternalCalls] = useState<
    Map<string, IFunctionCallEvent[]>
  >(new Map());

  // REST snapshot status and the tail cursor (newest event_time from history).
  // The journal stream waits for `snapshotReady`, then resumes from `cursor`.
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [journalCursor, setJournalCursor] = useState<string | null>(null);
  // Snapshot failure, surfaced to the UI rather than only to Sentry, and the
  // nonce that lets `retry` re-trigger the otherwise one-shot snapshot effect.
  const [snapshotError, setSnapshotError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // The journal tail opens only when the snapshot found the run still live. It
  // is NOT torn down the instant the run goes terminal — the idle-close below
  // retires it via `closeJournalRef` after a quiet window.
  const [journalTailEligible, setJournalTailEligible] = useState(false);

  // Set once the run is observed terminal (run_update stream or run query),
  // which stops the empty-state spinner via `isStreaming`.
  const [runReachedTerminal, setRunReachedTerminal] = useState(false);
  const runReachedTerminalRef = useRef(false);

  // `useStream` returns an imperative `close`; capture it via a ref so the
  // idle-close timer can tear the journal connection down directly.
  const closeJournalRef = useRef<(() => void) | null>(null);

  // Idle-close timer for the journal tail. A terminal run's journal SSE never
  // gets its own `completed` marker (the `node_kind` filter
  // drops it), so once the run is terminal we close the tail only after it has
  // gone quiet for JOURNAL_IDLE_CLOSE_MS — each new event resets the timer, so a
  // burst of final events is never cut off mid-delivery.
  const journalIdleCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const scheduleJournalClose = useCallback(() => {
    if (journalIdleCloseTimerRef.current) {
      clearTimeout(journalIdleCloseTimerRef.current);
    }
    journalIdleCloseTimerRef.current = setTimeout(() => {
      journalIdleCloseTimerRef.current = null;
      closeJournalRef.current?.();
    }, JOURNAL_IDLE_CLOSE_MS);
  }, []);

  // Appends a function-call step, deduped by node id. The REST snapshot and the
  // live tail both funnel through here, so the snapshot/tail boundary (and a
  // Last-Event-ID resume after reconnect) is idempotent. If the step's
  // `node_started` twin was already seen, stamp its start time on at insert.
  const appendEvent = useCallback((next: IFunctionCallEvent) => {
    // A looped book call repeats its node id; steps stay one row per call site.
    const eventKey = next.bookFunction ? invocationKey(next) : next.nodeId;
    if (seenInvocationKeysRef.current.has(eventKey)) {
      if (next.bookFunction) return;
      const latestExecuted = executedTimesRef.current.get(next.nodeId) ?? '';
      if (next.timestamp && next.timestamp > latestExecuted) {
        executedTimesRef.current.set(next.nodeId, next.timestamp);
      }
      // A later loop iteration of a step finishing: close it like the first.
      if (openStepIdsRef.current.includes(next.nodeId)) {
        lastStepIdRef.current = next.nodeId;
        openStepIdsRef.current = openStepIdsRef.current.filter(
          id => id !== next.nodeId,
        );
      }
      return;
    }
    seenInvocationKeysRef.current.add(eventKey);
    const startedTimestamp =
      next.startedTimestamp ?? startedTimesRef.current.get(next.nodeId);
    const event = { ...next, startedTimestamp };

    // Attribution is by enclosing step, not source line, so a call made from a
    // helper at any depth still lands on the right step.
    if (event.bookFunction) {
      const parentStepId =
        openStepIdsRef.current.at(-1) ?? lastStepIdRef.current;
      // Nothing to attach to; books are always called from step bodies.
      if (!parentStepId) return;
      if (seenInvocationKeysRef.current.has(parentStepId)) {
        // Parent already appended by an out-of-order replay; patch it in place.
        setEvents(prev =>
          prev.map(step =>
            step.nodeId === parentStepId
              ? {
                  ...step,
                  externalCalls: [...(step.externalCalls ?? []), event],
                }
              : step,
          ),
        );
        return;
      }
      const pending = [
        ...(pendingExternalCallsRef.current.get(parentStepId) ?? []),
        event,
      ];
      pendingExternalCallsRef.current.set(parentStepId, pending);
      setPendingExternalCalls(prev => new Map(prev).set(parentStepId, pending));
      return;
    }

    // Claim any external calls that ran while this step was open.
    const externalCalls = pendingExternalCallsRef.current.get(next.nodeId);
    if (externalCalls) {
      pendingExternalCallsRef.current.delete(next.nodeId);
      setPendingExternalCalls(prev => {
        const remaining = new Map(prev);
        remaining.delete(next.nodeId);
        return remaining;
      });
      event.externalCalls = externalCalls;
    }

    if (next.timestamp) {
      executedTimesRef.current.set(next.nodeId, next.timestamp);
    }
    lastStepIdRef.current = next.nodeId;
    openStepIdsRef.current = openStepIdsRef.current.filter(
      id => id !== next.nodeId,
    );
    setEvents(prev => [...prev, event]);
    // The step has finished, so it is no longer the running one.
    setStartedSteps(prev =>
      prev.some(step => step.nodeId === next.nodeId)
        ? prev.filter(step => step.nodeId !== next.nodeId)
        : prev,
    );
  }, []);

  // Records a step's `node_started` marker: stashes its start time (consumed by
  // `appendEvent` when the executed event lands) and marks the step as running.
  // If the step is already in state (the started event arrived out of order, e.g.
  // a REST replay), patch its start time in place and treat it as finished — its
  // `node_executed` has already been seen, so it is not running.
  const recordStarted = useCallback(
    ({
      nodeId,
      startedTimestamp,
      codeLocation,
      nodeKind,
    }: INodeStartedEvent) => {
      startedTimesRef.current.set(nodeId, startedTimestamp);
      // Keep the start time for the call's duration, but it is not a step:
      // `startedSteps`' last entry is `runningStep` and drives the AOP marker.
      if (nodeKind === ENodeKind.BookFunctionCall) return;
      if (!seenInvocationKeysRef.current.has(nodeId)) {
        if (!openStepIdsRef.current.includes(nodeId)) {
          openStepIdsRef.current = [...openStepIdsRef.current, nodeId];
        }
        setStartedSteps(prev =>
          prev.some(step => step.nodeId === nodeId)
            ? prev
            : [...prev, { nodeId, startedTimestamp, codeLocation, nodeKind }],
        );
        return;
      }
      const latestExecuted = executedTimesRef.current.get(nodeId);
      // A later loop iteration: reopen it for attribution, keep the row's times.
      if (latestExecuted !== undefined && startedTimestamp > latestExecuted) {
        if (!openStepIdsRef.current.includes(nodeId)) {
          openStepIdsRef.current = [...openStepIdsRef.current, nodeId];
        }
        return;
      }
      // Only move the start earlier: a replayed later iteration must not win.
      setEvents(prev =>
        prev.map(e =>
          e.nodeId === nodeId &&
          (e.timestamp === undefined || startedTimestamp <= e.timestamp) &&
          (e.startedTimestamp === undefined ||
            startedTimestamp < e.startedTimestamp)
            ? { ...e, startedTimestamp }
            : e,
        ),
      );
    },
    [],
  );

  // Paginated REST history fetch. Appends every FunctionCall step (deduped) and
  // returns the newest `event_time` seen — the cursor handed to the tail stream
  // via Last-Event-ID so it resumes with only newer events. RFC3339-nanosecond
  // timestamps sort lexicographically, so a string max is the latest event.
  const fetchHistory = useCallback(async (): Promise<string | null> => {
    if (
      !currentOrganizationId ||
      !currentWorkspaceId ||
      !automationId ||
      !runId ||
      !mainBounds
    ) {
      return null;
    }
    // REST needs `event_type` in the filter (no X-Event-Type header here), and
    // its query parser requires AIP-160 spaces as `%20`. `serializeParams` (qs)
    // encodes that way; axios' default would emit `+` and the endpoint 500s.
    const filter = buildJournalScopeFilter(mainBounds, { eventType: true });
    let cursor: string | null = null;
    let pageToken: string | undefined;
    do {
      const response = await listRunEvents(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
        runId,
        { page_size: 1000, page_token: pageToken, filter },
        { paramsSerializer: serializeParams },
      );
      for (const runEvent of response.data.run_events ?? []) {
        const eventTime = runEvent.execution_journal?.event_time;
        if (eventTime && (cursor === null || eventTime > cursor)) {
          cursor = eventTime;
        }
        const started = extractNodeStartedEventFromRunEvent(runEvent);
        if (started) recordStarted(started);
        const next = extractFunctionCallEventFromRunEvent(runEvent);
        if (next) appendEvent(next);
      }
      pageToken = response.data.next_page_token || undefined;
    } while (pageToken);
    return cursor;
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    runId,
    mainBounds,
    appendEvent,
    recordStarted,
  ]);

  // Reset all per-run state when the run changes.
  // eslint-disable-next-line no-restricted-syntax -- reason: reset derived state on run change
  useEffect(() => {
    seenInvocationKeysRef.current = new Set();
    startedTimesRef.current = new Map();
    executedTimesRef.current = new Map();
    pendingExternalCallsRef.current = new Map();
    setPendingExternalCalls(new Map());
    openStepIdsRef.current = [];
    lastStepIdRef.current = null;
    setEvents([]);
    setStartedSteps([]);
    setSnapshotReady(false);
    setSnapshotError(null);
    setJournalCursor(null);
    setJournalTailEligible(false);
    setRunReachedTerminal(false);
    runReachedTerminalRef.current = false;
    if (journalIdleCloseTimerRef.current) {
      clearTimeout(journalIdleCloseTimerRef.current);
      journalIdleCloseTimerRef.current = null;
    }
  }, [automationId, runId]);

  // Snapshot: always load the full filtered history over REST first, then flip
  // `snapshotReady` so the tail stream opens with the resulting cursor. Runs
  // once per run; the SSE then only ever carries events newer than this.
  const snapshotStartedRef = useRef(false);
  // eslint-disable-next-line no-restricted-syntax -- reason: reset one-shot snapshot guard on run change
  useEffect(() => {
    snapshotStartedRef.current = false;
  }, [automationId, runId]);

  /**
   * Re-runs the snapshot after a failure.
   *
   * Bumping the nonce is what re-triggers the one-shot effect; clearing the
   * ref alone would not, since the effect's other deps are unchanged. Events
   * are deliberately kept — `appendEvent` dedupes, so a retry
   * after a partial page fetch tops up rather than duplicating.
   */
  const retry = useCallback(() => {
    snapshotStartedRef.current = false;
    setSnapshotError(null);
    setSnapshotReady(false);
    setRetryNonce(nonce => nonce + 1);
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- reason: imperative REST snapshot that seeds events before the tail stream opens
  useEffect(() => {
    if (snapshotStartedRef.current) return;
    if (!currentOrganizationId || !currentWorkspaceId) return;
    if (!automationId || !runId || !mainBounds) return;
    snapshotStartedRef.current = true;

    let cancelled = false;
    void (async () => {
      let cursor: string | null = null;
      try {
        cursor = await fetchHistory();
        if (!cancelled) setSnapshotError(null);
      } catch (err) {
        captureException(err as Error, {
          tags: { module: 'runs', action: 'list_run_events_snapshot' },
          extra: { automationId, runId },
        });
        // Surface it too. Reporting to Sentry alone left the UI unable to tell
        // "this run has no steps" apart from "we could not load them".
        if (!cancelled) setSnapshotError(err as Error);
      }
      // Open the tail regardless of fetch outcome: on error `cursor` is null and
      // the stream replays from the start (degrades to the old behavior). Only
      // tail when the run was still live at snapshot time — an already-terminal
      // run has its complete history in the snapshot, so no stream is needed.
      if (cancelled) return;
      setJournalCursor(cursor);
      // `stopped` is resumable — still tail the stream for ContinueRun.
      setJournalTailEligible(!isRunInFinalState(runStateRef.current));
      setSnapshotReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    runId,
    mainBounds,
    fetchHistory,
    retryNonce,
  ]);

  const handleJournalMessage = useCallback(
    (message: IStreamMessage) => {
      // The server-side `buildJournalScopeFilter` scopes both the resumed replay
      // and live events to main's FunctionCall nodes, so no client-side line
      // re-check is needed here.
      // A step emits `node_started` (its start time and call site) before
      // `node_executed`; record it so `appendEvent` can stamp the start onto the
      // step, and so the step reads as running until its executed event lands.
      const started = extractNodeStartedEvent(message);
      if (started) recordStarted(started);
      const next = extractFunctionCallEvent(message);
      if (next) appendEvent(next);

      // If the run already went terminal, keep deferring the journal close while
      // final events are still arriving — close only after it falls quiet.
      if (runReachedTerminalRef.current) scheduleJournalClose();
    },
    [appendEvent, recordStarted, scheduleJournalClose],
  );

  const handleRunUpdateMessage = useCallback(
    (message: IStreamMessage) => {
      const run = extractRunUpdate(message);
      if (!run) return;
      onRunStateUpdateRef.current?.(run);
      // A run_update means the run state (including any awaiting-guidance
      // exception) may have changed server-side. Invalidate cached exception
      // data so it's refetched with the latest state/description.
      queryClient.invalidateQueries({
        queryKey: getListExceptionsQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
        ),
      });
      // Latch only on a genuinely-final completion. `stopped` is resumable, and
      // an intermediate step `completed` isn't the run finishing — latching there
      // would close the streams before the next step's `awaiting_guidance` arrives.
      if (isRunEffectivelyFinal(run, totalStepsRef.current)) {
        runReachedTerminalRef.current = true;
        setRunReachedTerminal(true);
        scheduleJournalClose();
      }
    },
    [
      scheduleJournalClose,
      queryClient,
      currentOrganizationId,
      currentWorkspaceId,
    ],
  );

  const handleError = useCallback((err: IStreamError) => {
    captureException(err.error, {
      tags: { module: 'runs', action: 'stream_run_function_events' },
      extra: { reason: err.reason, httpStatus: err.httpStatus },
    });
  }, []);

  // A final run's history is fully in the snapshot — no live stream needed.
  // `stopped` is excluded so resume can re-open the stream.
  const isTerminal = isRunInFinalState(runState) || runReachedTerminal;

  const baseStreamUrl =
    currentOrganizationId && currentWorkspaceId && automationId && runId
      ? `/api/v1/organizations/${currentOrganizationId}/workspaces/${currentWorkspaceId}/automations/${automationId}/runs/${runId}/events:stream`
      : null;

  // Journal tail: opens once the snapshot resolves and the run was live then.
  // The URL is intentionally NOT gated on terminal state — the stream stays open
  // past completion so trailing events arrive, and is closed imperatively by the
  // idle-close timer (`closeJournalRef`). `lastEventId` (the snapshot cursor)
  // makes the server resume with only events newer than the snapshot, so history
  // never double-streams.
  const journalStreamUrl =
    baseStreamUrl && mainBounds && snapshotReady && journalTailEligible
      ? `${baseStreamUrl}?${new URLSearchParams({
          filter: buildJournalScopeFilter(mainBounds),
        }).toString()}`
      : null;

  const baseStreamConfig = {
    protocol: EStreamProtocol.SSE,
    auth: {
      getToken: () => getAccessTokenSilentlyRef.current(),
      e2eTestMode: isE2ETestMode(),
    },
    retry: {
      maxAttempts: 5,
      strategy: ERetryStrategy.EXPONENTIAL,
      baseDelayMs: 1000,
    },
    // 410 Gone is permanent. This endpoint intermittently 404s while a run is
    // still active, and a fatal close drops the only run_update terminal
    // signal, so retry instead of failing (MAN-3735).
    fatalHttpStatuses: [410],
    retryableHttpStatuses: [404],
    parse: parseMessage,
    onError: handleError,
  };

  const { state, close: closeJournal } = useStream<IStreamMessage>(
    journalStreamUrl
      ? {
          ...baseStreamConfig,
          url: journalStreamUrl,
          lastEventId: journalCursor ?? undefined,
          headers: { 'X-Event-Type': 'execution_journal' },
          observability: {
            module: 'runs',
            operation: 'stream_run_function_events',
          },
          onMessage: handleJournalMessage,
        }
      : null,
  );
  closeJournalRef.current = closeJournal;

  // run_update tail: live run-state transitions (awaiting-guidance, terminal)
  // while the run is active. Skipped once terminal — state is then known from
  // the run query. No filter: those events lack start_line/end_line, and the
  // server drops events missing the filtered fields.
  useStream<IStreamMessage>(
    baseStreamUrl && !isTerminal
      ? {
          ...baseStreamConfig,
          url: baseStreamUrl,
          headers: { 'X-Event-Type': 'run_update' },
          observability: {
            module: 'runs',
            operation: 'stream_run_state_updates',
          },
          onMessage: handleRunUpdateMessage,
        }
      : null,
  );

  // Clear the journal idle-close timer on unmount so a pending close can't fire
  // against a torn-down stream.
  // eslint-disable-next-line no-restricted-syntax -- reason: timer cleanup tied to stream lifecycle, not derivable as state
  useEffect(() => {
    return () => {
      if (journalIdleCloseTimerRef.current) {
        clearTimeout(journalIdleCloseTimerRef.current);
        journalIdleCloseTimerRef.current = null;
      }
    };
  }, []);

  // Streaming while we're loading the snapshot or tailing a live run; never on a
  // terminal run (the spinner stops immediately even though the tail may stay
  // open briefly to drain final events).
  const isStreaming =
    !isTerminal &&
    (!snapshotReady ||
      state === EStreamState.CONNECTING ||
      state === EStreamState.CONNECTED ||
      state === EStreamState.RECONNECTING);

  // The snapshot is the authoritative load; until it settles we genuinely do
  // not know whether this run has steps. `isStreaming` cannot stand in for
  // this because it is false for terminal runs from the first render.
  //
  // Gated on the same inputs the snapshot effect requires: without them it bails
  // before ever setting `snapshotReady`, and a bare `!snapshotReady` would then
  // spin forever — e.g. a terminal run whose manifest has no `main.location`, so
  // `mainBounds` never resolves.
  const isLoading =
    !!currentOrganizationId &&
    !!currentWorkspaceId &&
    !!automationId &&
    !!runId &&
    !!mainBounds &&
    !snapshotReady;

  // A tail that exhausted its retries means later steps may be missing. The
  // snapshot error takes precedence: it means we may have nothing at all.
  const error =
    snapshotError ??
    (state === EStreamState.FAILED
      ? new Error('Run event stream failed')
      : null);

  // The latest started-but-unfinished step. Steps run sequentially inside main,
  // so the last entry is the one in flight when several are somehow pending.
  const runningStep = startedSteps.at(-1) ?? null;
  const runningStepExternalCalls = runningStep
    ? (pendingExternalCalls.get(runningStep.nodeId) ?? NO_EXTERNAL_CALLS)
    : NO_EXTERNAL_CALLS;

  return {
    events,
    runningStep,
    runningStepExternalCalls,
    isStreaming,
    isLoading,
    error,
    retry,
  };
}
